import "./UseCaseBrowsePage.scss";

import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { BrowseEmptyState } from "./components/BrowseEmptyState";
import { BrowseFilters } from "./components/BrowseFilters";
import { BrowseHero } from "./components/BrowseHero";
import { UseCaseCard } from "./components/UseCaseCard";
import { UseCaseSection } from "./components/UseCaseSection";
import { ViewMoreCard } from "./components/ViewMoreCard";
import {
  USE_CASE_SORT_OPTIONS,
  USE_CASES_CARDS_PER_PAGE,
  USE_CASES_PAGINATION_PAGE_COUNT,
  UseCaseBrowseQueryParams,
  UseCaseBrowseTouchpoint,
} from "./constants";
import { useInfiniteScroll } from "./hooks/useInfiniteScroll";
import { useToolNames } from "./hooks/useToolNames";
import { useUseCaseBrowse } from "./hooks/useUseCaseBrowse";
import { useUseCaseInsights } from "./hooks/useUseCaseInsights";
import { useUseCaseMetadata } from "./hooks/useUseCaseMetadata";

const SORT_OPTIONS: { label: string; value: string }[] = USE_CASE_SORT_OPTIONS.map(({ label }) => ({
  label,
  value: label,
}));
const DEFAULT_SORT: string = SORT_OPTIONS[0].label;

const UseCaseBrowsePage: FC = () => {
  const { categories, recommendedAudience, labelMap } = useUseCaseMetadata();
  const location = useLocation();

  // Initialize state from URL params
  const initialParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      query: params.get(UseCaseBrowseQueryParams.QUERY) || "",
      sort: params.get(UseCaseBrowseQueryParams.SORT) || DEFAULT_SORT,
      categories: params.get(UseCaseBrowseQueryParams.CATEGORIES)?.split(",").filter(Boolean) || [],
      audience: params.get(UseCaseBrowseQueryParams.AUDIENCE)?.split(",").filter(Boolean) || [],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchQuery, setSearchQuery] = useState(initialParams.query);
  const [selectedJobFamilies, setSelectedJobFamilies] = useState<string[]>(initialParams.audience);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialParams.categories);
  const [sortBy, setSortBy] = useState<string>(initialParams.sort);
  const [isExpanded, setIsExpanded] = useState(false);
  const [articlePage, setArticlePage] = useState(1);
  const articleSentinelRef = useRef<HTMLDivElement | null>(null);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set(UseCaseBrowseQueryParams.QUERY, searchQuery);
    if (sortBy !== DEFAULT_SORT) params.set(UseCaseBrowseQueryParams.SORT, sortBy);
    if (selectedCategories.length) params.set(UseCaseBrowseQueryParams.CATEGORIES, selectedCategories.join(","));
    if (selectedJobFamilies.length) params.set(UseCaseBrowseQueryParams.AUDIENCE, selectedJobFamilies.join(","));

    const search = params.toString();
    const newUrl = search ? `${location.pathname}?${search}` : location.pathname;
    window.history.replaceState({}, "", newUrl);
  }, [searchQuery, sortBy, selectedCategories, selectedJobFamilies, location.pathname]);

  useEffect(() => {
    setArticlePage(1);
  }, [searchQuery, selectedCategories, selectedJobFamilies, sortBy]);

  const { items, isLoading, isLoadingMore, hasMoreItems, totalCount, loadMore } = useUseCaseBrowse({
    searchQuery,
    selectedCategories,
    selectedJobFamilies,
    sortBy,
  });

  useInfiniteScroll({
    sentinelRef: articleSentinelRef,
    isActive: isExpanded,
    isLoading: isLoadingMore,
    hasMoreItems,
    onLoadMore: loadMore,
  });

  const allToolIds = useMemo(() => {
    const ids = new Set<string>();
    items.forEach((uc) => uc.associatedToolIds?.forEach((id) => ids.add(id)));
    return Array.from(ids);
  }, [items]);
  const toolNames = useToolNames(allToolIds);
  const { globalStats } = useUseCaseInsights([]);
  const numberOfPages = Math.min(Math.ceil(items.length / USE_CASES_CARDS_PER_PAGE), USE_CASES_PAGINATION_PAGE_COUNT);

  const handleExpand = useCallback(() => {
    setIsExpanded(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleCollapse = useCallback(() => {
    setIsExpanded(false);
    setArticlePage(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleJobFamilyChange = useCallback((selection: unknown) => {
    setSelectedJobFamilies(Array.isArray(selection) ? (selection as string[]) : []);
  }, []);

  const handleCategoriesChange = useCallback((selection: unknown) => {
    setSelectedCategories(Array.isArray(selection) ? (selection as string[]) : []);
  }, []);

  const handleSortChange = useCallback((selection: unknown) => {
    if (typeof selection === "string" && selection) {
      setSortBy(selection);
    }
  }, []);

  const renderCards = () => {
    if (isExpanded) {
      return items.map((useCase) => (
        <UseCaseCard key={useCase.taxonomyId} useCase={useCase} toolNames={toolNames} labelMap={labelMap} />
      ));
    }

    const isLastPage = articlePage === numberOfPages;
    const hasMore = items.length > numberOfPages * USE_CASES_CARDS_PER_PAGE;
    const showViewMore = isLastPage && hasMore;
    const pageStart = (articlePage - 1) * USE_CASES_CARDS_PER_PAGE;
    const pageItems = items.slice(
      pageStart,
      pageStart + (showViewMore ? USE_CASES_CARDS_PER_PAGE - 1 : USE_CASES_CARDS_PER_PAGE)
    );

    return (
      <>
        {pageItems.map((useCase) => (
          <UseCaseCard key={useCase.taxonomyId} useCase={useCase} toolNames={toolNames} labelMap={labelMap} />
        ))}
        {showViewMore && <ViewMoreCard onExpand={handleExpand} />}
      </>
    );
  };

  return (
    <div className="use-case-browse-page">
      <BrowseHero searchQuery={searchQuery} onSearchChange={setSearchQuery} globalStats={globalStats} />

      <PageMainContainer>
        <BrowseFilters
          jobFamilies={recommendedAudience}
          categories={categories}
          selectedJobFamilies={selectedJobFamilies}
          selectedCategories={selectedCategories}
          sortBy={sortBy}
          defaultSortLabel={DEFAULT_SORT}
          sortOptions={SORT_OPTIONS}
          onJobFamilyChange={handleJobFamilyChange}
          onCategoriesChange={handleCategoriesChange}
          onSortChange={handleSortChange}
        />

        {isLoading && <Spinner size={SpinnerSize.Medium} />}

        {!isLoading && items.length === 0 && <BrowseEmptyState />}

        {!isLoading && items.length > 0 && (
          <UseCaseSection
            title={getString("useCases.browsePage.articleSectionTitle")}
            totalCount={totalCount}
            isHidden={false}
            isExpanded={isExpanded}
            numberOfPages={numberOfPages}
            sentinelRef={articleSentinelRef}
            page={articlePage}
            onPageSelect={setArticlePage}
            paginationAriaLabel={getString("useCases.browsePage.articlePaginationAriaLabel")}
            exploreLabel={getString("useCases.browsePage.exploreAllArticles")}
            exploreTouchpoint={UseCaseBrowseTouchpoint.ExploreAllArticles}
            onExplore={handleExpand}
            collapseLabel={getString("useCases.browsePage.showAllSections")}
            collapseTouchpoint={UseCaseBrowseTouchpoint.CollapseSection}
            onCollapse={handleCollapse}
          >
            {renderCards()}
          </UseCaseSection>
        )}
      </PageMainContainer>
    </div>
  );
};

export default UseCaseBrowsePage;
